import type {Context, NodePatchResult} from '../types';
import type {ExtraContext} from '../ExtraContext';
import type {JSONNode} from '@packages/shared/types/JSONNode';
import {
  Expressions,
  Types,
  type Type,
  type ExpressionPath,
  Plugins,
} from '@packages/systems/wfdl';
import {
  EList,
  ERecord,
  EText,
  type Expression,
} from '@packages/systems/wfdl-ast';
import {constructAppend, deconstructAppend} from '@packages/utilities/append';
import {fixDataTypeJson} from '@packages/systems/core/component-migrations';
import {
  isSymbolRoot,
  isSymbolInst,
} from '@packages/systems/core/utils/DomTypeUtils';

/**
 * Migrates the deprecated `Devlink::RuntimeProps` datatype to `Basic::Attributes`
 * on its three persisted surfaces, all in one version:
 *
 *  - Component root declaration: the stringified `jsonNode.dataType` field type
 *    (`Devlink::RuntimeProps` -> `Basic::Attributes`, `defaultValue` -> `[]`),
 *    via the `migrateDevlinkRuntimePropsDataType` leaf in `fixDataTypeJson`.
 *  - Component instance: the stringified `data.sym.instDataType` (same leaf)
 *    and any bare-Select `bind.sym.overrides.<propId>` for a RuntimeProps- or
 *    Attributes-typed prop, wrapped into `Append(EList([]), Select)`. The
 *    Attributes match is required because `ExtraContext.dataTypes` is built
 *    by `dataTypesFromSymbolNodes`, whose `fixDataTypeJson` includes this
 *    patch's declaration leaf — a legacy RuntimeProps prop already reports as
 *    `Basic::Attributes` there. A bare Select is the legacy signature — the
 *    old binding UI only ever wrote Selects, while a natively-bound
 *    Attributes override is always an `Append` chain (the editor's
 *    `computeBindExpression`) — so wrapping only Selects leaves native
 *    bindings untouched and keeps the rewrite idempotent.
 *  - Element binding: a bound `bind.devlink.runtimeProps` `Select`, moved to the
 *    `xattr` channel as `Append(EList([static]), Select)`.
 *
 * Important notes:
 *
 *  - Both declaration rewrites are gated on `RUNTIME_PROPS_MARKER` and never
 *    throw: the patch runs in-memory on the SSR read path
 *    (`getMigratedSiteJson`), so a malformed persisted declaration is skipped
 *    rather than failing every render of the site.
 *  - Accepted edge: for a site still below v94, patch 094 re-persists
 *    `instDataType` through today's `fixDataTypeJson` (this patch's leaf
 *    included) earlier in the same pass, erasing `RUNTIME_PROPS_MARKER` — so
 *    a bound override on such an instance stays a bare Select and its spread
 *    is dropped from future DevLink exports. Deliberately not handled: the
 *    affected population is sites that used RuntimeProps prop drilling before
 *    094 landed (2023-11-30) and have not been opened in the Designer since,
 *    and the impact only materializes if such a site is revived and
 *    re-exported.
 *  - Custom Element / HTML Embed (`Builtin::DOM`/`DangerousDOM`) never carry a
 *    `devlink.runtimeProps` binding (`Atom.create`, not `createDevlinkAtom`) —
 *    their element surface is always `xattr`.
 */

const DEVLINK_RUNTIME_PROPS_PATH: ExpressionPath = [
  {in: 'Record', at: 'devlink'},
  {in: 'Record', at: 'runtimeProps'},
];

const XATTR_PATH: ExpressionPath = [{in: 'Record', at: 'xattr'}];

const SYM_OVERRIDES_PATH: ExpressionPath = [
  {in: 'Record', at: 'sym'},
  {in: 'Record', at: 'overrides'},
];

// The stringified `TypeConstructor` marker for `Devlink::RuntimeProps`, used as a
// cheap pre-filter before parsing a multi-KB dataType/instDataType (mirrors 108).
const RUNTIME_PROPS_MARKER = '["Devlink","RuntimeProps"]';

const parseIfString = (value: string | Type) =>
  typeof value === 'string' ? JSON.parse(value) : {...value};

// Cheap pre-filter: stringified declarations are marker-checked without a
// parse; non-string (rare legacy) values pass through to the rewrite.
const mayContainRuntimeProps = (value: string | Type): boolean =>
  typeof value !== 'string' || value.indexOf(RUNTIME_PROPS_MARKER) > -1;

// Never throws (see the SSR-read-path note in the file header); a malformed
// persisted declaration is skipped by returning undefined.
const fixStringifiedDataTypeGuarded = (
  value: string | Type
): string | undefined => {
  try {
    return JSON.stringify(fixDataTypeJson(parseIfString(value)));
  } catch {
    return undefined;
  }
};

const overridePath = (propId: string): ExpressionPath => [
  ...SYM_OVERRIDES_PATH,
  {in: 'Record', at: propId},
];

/**
 * Builds the static `CustomAttribute` list from a persisted `data.xattr` POJO
 * (`[{name, value}]`). These pre-existing static attributes become the inner
 * list of the `Append`, so they survive alongside the migrated binding.
 *
 * `data.xattr` is guaranteed to be fully-static string pairs: the
 * Expression->JSONNode serializer (`splitDataBindExpressionMatcherForElement`)
 * is all-or-nothing per list — any bound item sub-field moves the whole list
 * to `bind.xattr` and leaves `data.xattr` absent. So a plain `EText` per field
 * matches every writer; non-object entries (corrupt data) are skipped rather
 * than invented as empty attributes.
 */
const buildStaticAttributesList = (xattr: unknown): Expression => {
  if (!Array.isArray(xattr) || xattr.length === 0) {
    return EList([]);
  }
  return EList(
    xattr
      .filter(
        (item): item is {name?: unknown; value?: unknown} =>
          typeof item === 'object' && item !== null
      )
      .map((item) =>
        ERecord({
          name: EText(String(item.name ?? '')),
          value: EText(String(item.value ?? '')),
        })
      )
  );
};

export default function patchDevlinkRuntimePropsToAttributes(
  context: Context,
  {reportWarning}: {reportWarning?: ExtraContext['reportWarning']} = {}
): NodePatchResult {
  const {jsonNode} = context.node;
  const {dataTypes} = context.extraContext;

  if (jsonNode.type === 'Symbol') {
    return patchComponentInstance(jsonNode, dataTypes, reportWarning);
  }
  return patchElementAndRoot(jsonNode);
}

function patchElementAndRoot(jsonNode: JSONNode): NodePatchResult {
  // (1) Component-root prop declaration in the stringified `dataType`.
  let fixedDataType: string | undefined;
  let fixedDataTypeNeeded = false;
  if (
    jsonNode.dataType &&
    isSymbolRoot(jsonNode.data) &&
    mayContainRuntimeProps(jsonNode.dataType)
  ) {
    fixedDataType = fixStringifiedDataTypeGuarded(jsonNode.dataType);
    fixedDataTypeNeeded =
      fixedDataType !== undefined && fixedDataType !== jsonNode.dataType;
  }

  // (2) Element binding: bound `devlink.runtimeProps` Select.
  let elementBindingNeeded = false;
  if (jsonNode.bind) {
    const rpExpr = Expressions.getIn(jsonNode.bind, DEVLINK_RUNTIME_PROPS_PATH);
    elementBindingNeeded = Boolean(rpExpr && Expressions.isBinding(rpExpr));
  }

  return {
    needed: fixedDataTypeNeeded || elementBindingNeeded,
    logData: {
      msg: 'Devlink RuntimeProps -> Attributes: element/root declaration',
      meta: {nodeId: jsonNode._id},
    },
    apply: () => {
      if (fixedDataTypeNeeded && isSymbolRoot(jsonNode.data)) {
        jsonNode.dataType = fixedDataType;
      }

      if (elementBindingNeeded && jsonNode.bind) {
        const rpExpr = Expressions.getIn(
          jsonNode.bind,
          DEVLINK_RUNTIME_PROPS_PATH
        );
        if (rpExpr && Expressions.isBinding(rpExpr)) {
          // A `bind.xattr` expression can be two very different things:
          //  - the whole list bound to a prop (an `Append` chain, or a bare
          //    binding): a real conflict with the RuntimeProps binding. The
          //    legacy editor blocked this ("Can't bind Attributes when Runtime
          //    Props are connected"), so it shouldn't exist — but if it does,
          //    drop only the RuntimeProps binding and leave the existing
          //    Attributes binding (and its data) untouched.
          //  - a plain `EList` that lives in `bind` only because an item's
          //    sub-field (e.g. a value) is bound: not a list-level binding at
          //    all. The serializer is all-or-nothing, so one bound value moves
          //    the whole list here. Fold the RuntimeProps binding on top of it,
          //    exactly like the static `data.xattr` case below.
          const existingXattr = Expressions.getIn(jsonNode.bind, XATTR_PATH);
          if (existingXattr) {
            const isListLevelBound =
              Expressions.isBinding(existingXattr) ||
              deconstructAppend(existingXattr).length > 1;
            if (isListLevelBound) {
              jsonNode.bind = Expressions.recursiveDeleteInRecord(
                jsonNode.bind,
                DEVLINK_RUNTIME_PROPS_PATH
              );
            } else {
              const append = constructAppend([existingXattr, rpExpr]);
              const withoutRuntimeProps = Expressions.recursiveDeleteInRecord(
                jsonNode.bind,
                DEVLINK_RUNTIME_PROPS_PATH
              );
              const fields = Expressions.getRecord(withoutRuntimeProps) ?? {};
              jsonNode.bind = ERecord({...fields, xattr: append});
            }
          } else {
            // static items first so the bound value overrides them, matching the
            // Attributes editor's `computeBindExpression`.
            const staticList = buildStaticAttributesList(jsonNode.data?.xattr);
            const append = constructAppend([staticList, rpExpr]);

            // Remove the runtimeProps binding first (pruning the now-empty
            // `devlink` record while preserving a `devlink.slot` binding if any),
            // then add `xattr` at the top level. `setIn` only updates existing
            // paths, so the new top-level key is added by rebuilding the record.
            const withoutRuntimeProps = Expressions.recursiveDeleteInRecord(
              jsonNode.bind,
              DEVLINK_RUNTIME_PROPS_PATH
            );
            const fields = Expressions.getRecord(withoutRuntimeProps) ?? {};
            jsonNode.bind = ERecord({...fields, xattr: append});

            // The static list now lives inside the Append; drop the POJO copy so
            // reification doesn't keep a stale static value alongside the binding.
            if (jsonNode.data && 'xattr' in jsonNode.data) {
              const {xattr: _movedStaticXattr, ...restData} = jsonNode.data;
              jsonNode.data = restData;
            }
          }
        }
      }
    },
  };
}

function patchComponentInstance(
  jsonNode: JSONNode,
  dataTypes: Plugins.DataTypes,
  reportWarning?: ExtraContext['reportWarning']
): NodePatchResult {
  // (1) Instance declaration in the stringified `instDataType`.
  let fixedInstDataType: string | undefined;
  let fixedInstDataTypeNeeded = false;
  if (
    isSymbolInst(jsonNode.data) &&
    jsonNode.data?.sym.instDataType &&
    mayContainRuntimeProps(jsonNode.data.sym.instDataType)
  ) {
    fixedInstDataType = fixStringifiedDataTypeGuarded(
      jsonNode.data.sym.instDataType
    );
    fixedInstDataTypeNeeded =
      fixedInstDataType !== undefined &&
      fixedInstDataType !== jsonNode.data.sym.instDataType;
  }

  // (2) Bound RuntimeProps prop overrides.
  const propIdsToMigrate: string[] = [];
  if (jsonNode.bind) {
    let dataType: Type | undefined;
    if (jsonNode.data && jsonNode.data.sym) {
      if (jsonNode.data.sym.instDataType) {
        if (typeof jsonNode.data.sym.instDataType !== 'string') {
          dataType = jsonNode.data.sym.instDataType;
        } else if (
          // avoid parsing the stringified Type object if we can
          jsonNode.data.sym.instDataType.indexOf(RUNTIME_PROPS_MARKER) > -1
        ) {
          try {
            dataType = JSON.parse(jsonNode.data.sym.instDataType);
          } catch {
            // malformed instDataType — skip the override migration too
            dataType = undefined;
          }
        }
        if (dataType) {
          dataType =
            Types.__DEPRECATED__getTypeAtExpressionPath(
              dataType,
              SYM_OVERRIDES_PATH
            ) || undefined;
        }
      } else if (typeof jsonNode.data.sym.inst === 'string') {
        // post-#100323 instances often lack instDataType — resolve the declared
        // prop types from the component root via ExtraContext.dataTypes. That
        // map is built by `dataTypesFromSymbolNodes`, which runs declarations
        // through `fixDataTypeJson` — so a legacy RuntimeProps prop reports as
        // `Basic::Attributes` here, never as `Devlink::RuntimeProps`.
        dataType = dataTypes.get(['__SitePlugin', jsonNode.data.sym.inst]);
        if (!dataType) {
          // The referenced component no longer exists (e.g. an instance-count
          // desync let deletion through while an instance survived on an
          // unloaded page). We can't tell which override, if any, was
          // RuntimeProps-typed without the component's dataType, so we skip
          // the override migration rather than risk dropping an unrelated,
          // still-valid override.
          reportWarning?.(
            'DevlinkRuntimePropsOrphanedInstance',
            `Symbol instance "${jsonNode._id}" references missing component "${jsonNode.data.sym.inst}" — cannot verify whether its bound overrides include an orphaned RuntimeProps binding.`
          );
        }
      }
    }

    if (dataType) {
      const fields = Types.getRecordFields(dataType);
      if (fields) {
        for (const propId of Object.keys(fields)) {
          const propType = fields[propId];
          if (
            propType &&
            (Types.isDevlinkRuntimePropsType(propType) ||
              Types.isAttributesType(propType))
          ) {
            const propExpr = Expressions.getIn(
              jsonNode.bind,
              overridePath(propId)
            );
            // A bare Select is the legacy-RuntimeProps signature: the old
            // binding UI only ever wrote Selects, while a natively-bound
            // Attributes override is always an `Append` chain (the editor's
            // `computeBindExpression`). Matching only Selects leaves native
            // bindings untouched and keeps the in-place rewrite idempotent.
            if (propExpr && Expressions.isSelect(propExpr)) {
              propIdsToMigrate.push(propId);
            }
          }
        }
      }
    }
  }

  return {
    needed: fixedInstDataTypeNeeded || propIdsToMigrate.length > 0,
    logData: {
      msg: 'Devlink RuntimeProps -> Attributes: component instance',
      meta: {nodeId: jsonNode._id},
    },
    apply: () => {
      if (fixedInstDataTypeNeeded && jsonNode.data?.sym) {
        jsonNode.data.sym.instDataType = fixedInstDataType;
      }

      if (propIdsToMigrate.length > 0 && jsonNode.bind) {
        let bind = jsonNode.bind;
        for (const propId of propIdsToMigrate) {
          const path = overridePath(propId);
          const propExpr = Expressions.getIn(bind, path);
          if (propExpr && Expressions.isSelect(propExpr)) {
            // a bound override becomes the bound-Attributes shape (no static items)
            bind = Expressions.setIn(
              bind,
              path,
              constructAppend([EList([]), propExpr])
            );
          }
        }
        jsonNode.bind = bind;
      }
    },
  };
}
